import { useEffect, useRef, useState } from "react";
import { Box, Card, CardContent, Typography, useMediaQuery,useTheme } from "@mui/material";
import { isEqual } from "lodash";
import { searchAssets } from "../../utils/API/Assets/Assets";
import { searchHistories } from "../../utils/API/AssetHistories/AssetHistories";
import {
  setDebouncedValue,
  setInitialResults,
  setResults,
  setSearchedValue,
  setSearchResults,
  setSearchText,
} from "../../globalStore/slices/user/userSlice";
import { thunks } from "../../globalStore/slices/inventory/inventorySlice";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { PieChart } from "@mui/x-charts/PieChart";
import GoogleMapComponent from "../../components/Maps/GoogleMapComponent";
import Grid from "@mui/material/Grid2";
import IndoorMap from "../facilities/Facilities/IndoorMap/IndoorMap";
import MaterialUiButton from "../../components/Buttons/MaterialUiButton/MaterialUiButton";
import MVPAssetsTable from "./table/MVPAssetsTable";
import PopUpMarkerContent from "../../components/Maps/PopUpMarkerContent";

// This component was created to satisfy a hard pivot on our console. The goal was the simply searching for an asset.
// Once we have a better idea of what new direction they want to go in, we can better optimize how we lay our structure and prop calls.

// Helper function to calculate squared Euclidean distance (for finding the closest point)
const calculateDistanceSquared = (lat1, lon1, lat2, lon2) => {
  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;
  return dLat * dLat + dLon * dLon; // Squared distance is sufficient for comparison
};

// Helper function to find an asset to center on (logic part)
const findAssetToCenterOnLogic = (assetsArray, userCoords = null) => {
  if (!assetsArray || assetsArray.length === 0) {
    return null;
  }
  // Filter for assets with lat/lon and no zone property
  const suitableAssets = assetsArray.filter(
    (asset) => asset.latitude && asset.longitude && !asset.zone
  );
  if (suitableAssets.length === 0) {
    return null;
  }
  if (userCoords) {
    let closestAsset = null;
    let minDistanceSq = Infinity;
    suitableAssets.forEach((asset) => {
      const distanceSq = calculateDistanceSquared(
        userCoords.lat,
        userCoords.lng,
        asset.latitude,
        asset.longitude
      );
      if (distanceSq < minDistanceSq) {
        minDistanceSq = distanceSq;
        closestAsset = asset;
      }
    });
    if (closestAsset) return closestAsset; // Return closest if found
  }
  return suitableAssets[0]; // Fallback to the first suitable asset if no userCoords or no closest found
};

export default function MVPAssets(props) {
  const { timeZone } = props;
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const dispatchGlobal = useDispatch();
  const { facilities } = useSelector((state) => state.organization, isEqual);
  const assetsStore = useSelector((state) => state.assets);
  const { filters: initFilters = {} } = assetsStore;
  const searchText = useSelector((state) => state.user.searchText);
  const zones = useSelector((state) => state.organization.zones, isEqual);
  // We use initial results to be the default pins if we need to default back to the original state.
  const initialResults = useSelector(
    (state) => state.user.initialResults,
    isEqual
  );
  const searchedValue = useSelector((state) => state.user.searchedValue);
  const initialState = {
    assetHistoryMarkers: [],
    devices: {},
    filters: {
      ...initFilters,
      endDate: null,
      limit: 10000,
      startDate: null,
    },
    assetHistories: [],
    isAssetHistory: false,
    markersToDisplay: [],

    // This is used to send users off to the assetSnapshot page
    selectedAssetId: "",

    // This is used for the facility pop up
    selectedAsset: {},
  };
  const [state, setState] = useState(initialState);
  const {
    assetHistories,
    assetHistoryMarkers,
    destination,
    isAssetHistory,
    markersToDisplay,
    origin = "2640 Claudia Ln, Theodore, AL 36582",
    selectedAssetId,
    selectedAsset,
  } = state;
  const [activeMarker, setActiveMarker] = useState(null);
  const [activeFacility, setActiveFacility] = useState(null);
  const [mapSettings, setMapSettings] = useState({
    center: {
      lat: 30.523385743642343, // Default latitude
      lng: -88.10271785113585, // Default longitude
    },
    mapTypeId: "satellite",
    zoom: 15,
  });
  const [showGoogleMap, setShowGoogleMap] = useState(true); // State to control map visibility
  const [initialCenterSet, setInitialCenterSet] = useState(false);
  const initialSearchTextOnLoadRef = useRef(null); // Stores searchText on initial component load
  const [userLocation, setUserLocation] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const viewPieChart = queryParams.get("viewPieChart") === "true";
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const shouldShowPieChart = viewPieChart && !isSmallScreen;

  // On mount, try to get user location once
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          setUserLocation(null);
        }
      );
    }
  }, []);

  // Main function to find and center map
  const findAndCenterMap = (assetsToConsider, isOperatingInASearchContext) => {
    if (!isOperatingInASearchContext) {
      setMapSettings({
        center: { lat: 39.8283, lng: -98.5795 },
        zoom: 4,
        mapTypeId: "satellite",
      });
      setInitialCenterSet(true);
      return;
    }

    // Proceed if in a search context
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const asset = findAssetToCenterOnLogic(assetsToConsider, {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });

          if (asset) {
            setMapSettings({
              center: { lat: asset.latitude, lng: asset.longitude },
              zoom: 17,
              mapTypeId: "satellite",
            });
          } else {
            // If no lat / lon available we default to the USA view
            setMapSettings({
              center: { lat: 39.8283, lng: -98.5795 },
              zoom: 4,
              mapTypeId: "satellite",
            });
          }

          setInitialCenterSet(true);
        },
        () => {
          // Geolocation error/denial
          const asset = findAssetToCenterOnLogic(assetsToConsider, null);

          if (asset) {
            // Set the map to the asset's lat/lon if available
            setMapSettings({
              center: { lat: asset.latitude, lng: asset.longitude },
              zoom: 15,
              mapTypeId: "satellite",
            });
          } else {
            // If no suitable asset found, default to the USA view
            setMapSettings({
              center: { lat: 39.8283, lng: -98.5795 },
              zoom: 4,
              mapTypeId: "satellite",
            });
          }

          setInitialCenterSet(true);
        }
      );
    } else {
      // Geolocation not supported
      const asset = findAssetToCenterOnLogic(assetsToConsider, null);
      if (asset) {
        // If we have an asset with lat/lon, center on it
        setMapSettings({
          center: { lat: asset.latitude, lng: asset.longitude },
          zoom: 15,
          mapTypeId: "satellite",
        });
      } else {
        // If no suitable asset found, default to the USA view
        setMapSettings({
          center: { lat: 39.8283, lng: -98.5795 },
          zoom: 4,
          mapTypeId: "satellite",
        });
      }
      setInitialCenterSet(true);
    }
  };
  const isMountedRef = useRef(false);
  const showLastEvents = thunks.assets.showLastEvents;

  const isSearchedValueSet =
    searchedValue && Object.keys(searchedValue).length > 0;
  const isClearSelectionButtonDisabled =
    activeMarker ||
    assetHistories?.length > 0 ||
    destination ||
    searchText.length > 0 ||
    isSearchedValueSet;
  const facilitiesArray = Object.values(facilities);

  // Handle row click: Set selected center, zoom in, and store the state
  const handleRowClick = (row) => {
    const {
      assetId = "",
      assetHistoryId,
      facilityId,
      latitude,
      longitude,
      zone,
    } = row?.row || {};

    // If the clicked asset is part of a facility, we want to have the facility pop up to open.
    if (zone && zone?.zoneId) {
      setActiveMarker(facilityId);
    } else {
      setActiveMarker(assetHistoryId ? assetHistoryId : assetId);
    }

    setState((prevState) => ({
      ...prevState,
      selectedAsset: row?.row,
      selectedAssetId: assetId,
    }));

    // Update the map settings to center on the row's lat/lon and zoom in
    if (latitude && longitude) {
      setMapSettings({
        center: { lat: latitude, lng: longitude },
        zoom: 20, // Adjust the zoom level as needed
      });
    }
  };

  const handleViewHistoryButtonClick = () => {
    if (isAssetHistory) {
      history.push(`/assetSnapshot/${selectedAssetId}`, { from: "/" });
    } else {
      searchHistories(props, { assetId: activeMarker }).then((res) => {
        const { assetHistories = [], success = false } = res || {};
        const newMarkers = convertPayloadToMarkers(assetHistories);

        if (success) {
          // Update state
          setState((prevState) => {
            return {
              ...prevState,
              assetHistories: assetHistories,
              assetHistoryMarkers: newMarkers,
              isAssetHistory: true,
            };
          });

          // Close whatever popup is open
          setActiveMarker(null);
        }
      });
    }
  };

  const popupContent = (popupData) => {
    return (
      <PopUpMarkerContent
        buttonLabel={
          isAssetHistory ? "View Asset Snapshot" : "View Asset History"
        }
        item={popupData}
        onButtonClick={handleViewHistoryButtonClick}
        onNavigateButtonClick={(item) => {
          if (userLocation) {
            const { latitude, longitude } = item || {};
            setState((prevState) => ({
              ...prevState,
              destination: `${latitude}, ${longitude}`,
              origin: `${userLocation.lat}, ${userLocation.lng}`,
            }));
          } else if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                setUserLocation({
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                });
                const { latitude, longitude } = item || {};
                setState((prevState) => ({
                  ...prevState,
                  destination: `${latitude}, ${longitude}`,
                  origin: `${position.coords.latitude}, ${position.coords.longitude}`,
                }));
              },
              () => {
                setUserLocation(null);
                // User denied again or error, do nothing
              }
            );
          }
          // If geolocation not supported, do nothing
        }}
      />
    );
  };

  // There's probably a better way of doing this. Need to do this for mock sake.
  // Initially popupContent was suppose to be modular depeneind on where the user clicks.
  // This is no longer true
  const facilityPopUpContent = (popupData) => {
    const facilityData = facilities[popupData?.facilityId] || {};

    return (
      <PopUpMarkerContent
        buttonLabel="View Facility Layout"
        clickedFacility={true}
        facility={facilityData}
        item={selectedAsset}
        onButtonClick={() => {
          setShowGoogleMap(false);
          setActiveFacility(facilityData?.facilityId);

          // Zoom the map to the facility location
          const { location = {} } = facilityData;
          const { latitude, longitude } = location;
          if (latitude && longitude) {
            setMapSettings({
              center: { lat: latitude, lng: longitude },
              zoom: 20, // or your preferred zoom level
              mapTypeId: "satellite",
            });
          }
        }}
        onButtonClick2={() => {
          history.push(`/assetSnapshot/${selectedAssetId}`, { from: "/" });
        }}
        onNavigateButtonClick={(facility) => {
          // Navigation logic for facility
          const { location = {} } = facilityData;
          const { latitude, longitude } = location;
          if (latitude && longitude) {
            if (userLocation) {
              setState((prevState) => ({
                ...prevState,
                destination: `${latitude}, ${longitude}`,
                origin: `${userLocation.lat}, ${userLocation.lng}`,
              }));
            } else if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  });
                  setState((prevState) => ({
                    ...prevState,
                    destination: `${latitude}, ${longitude}`,
                    origin: `${position.coords.latitude}, ${position.coords.longitude}`,
                  }));
                },
                () => {
                  setUserLocation(null);
                  // User denied again or error, do nothing
                }
              );
            }
            // If geolocation not supported, do nothing
          }
        }}
      />
    );
  };

  function convertPayloadToMarkers(payload) {
    return payload
      ?.map((event) => {
        // If the current event does not have a lat/lon, we check to see if a facilityId exist.
        // If so, we use the facilityId.location.lat and lon. Otherwise we use the event.lat and lon
        if (event.zone && event.zone.zoneId) {
          return null;
        } else if (
          event &&
          (event.latitude !== 0 || event.longitude !== 0) &&
          event.latitude &&
          event.longitude
        ) {
          return event;
        }

        // We no longer want to render events that do not have a lat/lon but do have a facility
        // else if (
        //   event &&
        //   (event.latitude !== 0 || event.longitude !== 0) &&
        //   event.facilityId
        // ) {
        //   const currentFacility = facilities[event.facilityId] || {};
        //   const { location = {} } = currentFacility;
        //   const { latitude, longitude } = location;
        //   return { ...event, latitude, longitude };
        // }
        else {
          return null;
        }
      })
      .filter((event) => event !== null)
      .sort((a, b) => a.timeOfLog - b.timeOfLog);
  }

  function clearSelection() {
    // No active marker should be open
    setActiveMarker(null);

    // Reset state to original settings.
    setState((prevState) => ({
      ...prevState,
      assetHistories: [],
      assetHistoryMarkers: [],
      destination: null,
      filters: {
        ...prevState.filters,
        endDate: null,
        startDate: null,
      },
      isAssetHistory: false,
      selectedAssetId: "",
      selectedAsset: {},
    }));

    // Clear redux searchedValue
    dispatchGlobal(setDebouncedValue(""));
    dispatchGlobal(setResults(initialResults));
    dispatchGlobal(setSearchedValue({}));
    dispatchGlobal(setSearchResults([]));
    dispatchGlobal(setSearchText(""));
    // After clearing search, explicitly set map to USA view if desired
    // and reset initialSearchTextOnLoadRef if you want "clear" to behave like a fresh non-search load
    // initialSearchTextOnLoadRef.current = null; // Optional: makes "clear" forget initial search state
    setInitialCenterSet(false); // Allow general centering effect to take over (which should default to USA)
  }

  function handleMarkerClick(marker) {
    setActiveMarker(
      marker.assetHistoryId ? marker.assetHistoryId : marker.assetId
    );
    setActiveFacility(marker.facilityId);
  }

  //iterate through facilities and get polygons to render
  const polygonsToRender = [];
  const floorPlanData = [];
  const circlesToRender = [];

  // Pie Chart data
  const series =
    state.devices?.assigned !== undefined &&
    state.devices?.unassigned !== undefined
      ? [
          {
            data: [
              { id: 0, value: state.devices.assigned, label: "Assigned" },
              { id: 1, value: state.devices.unassigned, label: "Unassigned" },
            ],
            innerRadius: 30,
            outerRadius: 100,
            paddingAngle: 5,
            cornerRadius: 5,
            startAngle: -45,
            endAngle: 500,
          },
        ]
      : [];
  facilitiesArray.forEach((facility) => {
    const { propertiesMap = {} } = facility;
    const { layoutData } = propertiesMap;

    if (layoutData) {
      // Check if layoutData has zones, geofences, or facilityOutlines
      if (
        layoutData.zones ||
        layoutData.geofences ||
        layoutData.facilityOutlines
      ) {
        // If it has zones, geofences, or facilityOutlines, we want to iterate over the geofences and facilityOutlines
        if (layoutData.geofences) {
          layoutData.geofences.forEach((geofence) => {
            polygonsToRender.push({ ...geofence, facility });
          });
        }
        if (layoutData.facilityOutlines) {
          layoutData.facilityOutlines.forEach((outline) => {
            polygonsToRender.push({ ...outline, facility });
          });
        }
        //if there is data in layoutData.floorPlans, add it to the floorPlanData array
        if (layoutData.floorPlans) {
          layoutData.floorPlans.forEach((floorPlan) => {
            floorPlanData.push({ ...floorPlan, facility });
          });
        }
        //if there are zones, we want to iterate over the zones and create circles
        if (layoutData.zones) {
          layoutData.zones.forEach((zone) => {
            // We only want to render zones that have a radius
            if (zone.radius) {
              circlesToRender.push({ ...zone, facility });
            }
          });
        }
      }
    }
  });

  // This handles the initial api call made to the /assets table and initial centering
  useEffect(() => {
    console.log("useEffect");
    if (!isMountedRef.current) {
      initialSearchTextOnLoadRef.current = searchText; // Capture initial searchText value

      dispatchGlobal(showLastEvents({ filters: state.filters })).then((res) => {
        const { assets: allAssets = [] } = res.payload || {};
        const initialRenderMarkers = convertPayloadToMarkers(allAssets);

        searchAssets(props, {
          filters: {
            assetMode: "Device",
            isSearchForDevice: true,
            limit: 10000,
          },
        }).then((deviceResponse) => {
          const devicesInfo = (deviceResponse.assets || []).reduce(
            (acc, asset) => {
              if (asset?.device?.hasDescendents === true) {
                acc.assigned += 1;
              } else {
                acc.unassigned += 1;
              }
              return acc;
            },
            { assigned: 0, unassigned: 0 }
          );

          setState((prevState) => ({
            ...prevState,
            markersToDisplay: initialRenderMarkers,
            devices: devicesInfo,
          }));
          dispatchGlobal(setInitialResults(allAssets));
          dispatchGlobal(setResults(allAssets)); // Set results for potential initial centering

          let assetsForInitialProcessing = allAssets;
          const wasInitialSearch =
            initialSearchTextOnLoadRef.current &&
            initialSearchTextOnLoadRef.current.length > 0;

          if (wasInitialSearch) {
            searchAssets(props, {
              filters: { tag: initialSearchTextOnLoadRef.current },
            }).then((searchRes) => {
              const { assets: searchedAssets = [], success = false } =
                searchRes || {};
              if (success) {
                const searchedMarkers = convertPayloadToMarkers(searchedAssets);
                setState((prevState) => ({
                  ...prevState,
                  markersToDisplay: searchedMarkers,
                }));
                dispatchGlobal(setResults(searchedAssets));
                assetsForInitialProcessing = searchedAssets;
              }
              // Center map after potential initial search, only if not already centered by another effect
              if (!initialCenterSet) {
                findAndCenterMap(assetsForInitialProcessing, true); // true because it's in an initial search context
              }
              isMountedRef.current = true;
            });
          } else {
            // No initial search text, so set default USA view.
            if (!initialCenterSet) {
              findAndCenterMap([], false); // Pass false to ensure USA view
            }
            isMountedRef.current = true;
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once on mount

  // General map centering effect for typing or no search (after initial load)
  useEffect(() => {
    if (initialCenterSet || !isMountedRef.current) {
      return; // Already centered or not ready
    }

    const searchedValueIsEmpty =
      !searchedValue || Object.keys(searchedValue).length === 0;
    const isTypingNewSearch =
      searchText && searchText.length > 0 && searchedValueIsEmpty;

    // If no search text, or user is typing a new search (and it's not an initial load with search text)
    if (!searchText || searchText.length === 0 || isTypingNewSearch) {
      setMapSettings({
        center: { lat: 39.8283, lng: -98.5795 },
        zoom: 4,
        mapTypeId: "satellite",
      });
      setInitialCenterSet(true);
    }
  }, [searchText, searchedValue, initialCenterSet, isMountedRef.current]);

  // Update useEffect to listen to searchedValue (for subsequent searches after initial load)
  useEffect(() => {
    if (
      !isMountedRef.current ||
      !searchedValue ||
      Object.keys(searchedValue).length === 0
    ) {
      // Do nothing if component hasn't finished initial mount or if searchedValue is empty
      return;
    }

    const {
      originalItem = {},
      label = "",
      type = "",
      value = "",
    } = searchedValue;
    let searchFilters = {};

    if (type === "Facility") searchFilters.facilityId = value;
    else if (type === "Zone") searchFilters.zones = [originalItem];
    else if (type === "Suggestion" || type === "Enter Key")
      searchFilters.tag = label;
    else if (type === "Event") searchFilters.lastEvent = [{ value: label }];

    // Important: Reset initialCenterSet to false *before* the async operation
    // that will eventually call findAndCenterMap.
    setInitialCenterSet(false);

    searchAssets(props, { filters: searchFilters }).then((res) => {
      const { assets = [], success = false } = res || {};
      if (success) {
        const newMarkers = convertPayloadToMarkers(assets);
        setState((prevState) => ({
          ...prevState,
          markersToDisplay: newMarkers,
        }));
        dispatchGlobal(setResults(assets));
        // Call findAndCenterMap for subsequent searches, indicating it's a search context
        findAndCenterMap(assets, true);
      } else {
        dispatchGlobal(setResults([]));
        findAndCenterMap([], true); // Still a search context, but no results
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedValue]); // props and dispatchGlobal are stable or managed by eslint

  return (
    <Grid container mt={4} mb={3}>
      {showGoogleMap ? (
        <>
          {/* Map */}
          <Grid
            className="custom-css"
            item
            size={shouldShowPieChart ? 8 : 12}
            sx={{
              marginBottom: "1rem",
              minHeight: "60vh",
            }}
          >
            <GoogleMapComponent
              activeMarker={activeMarker}
              facilities={facilitiesArray}
              facilityPopUpContent={facilityPopUpContent}
              handleMarkerClick={handleMarkerClick}
              mapSettings={mapSettings}
              mapTypeId="satellite"
              markerKey={isAssetHistory ? "assetHistoryId" : "assetId"}
              markersToRender={
                isAssetHistory ? assetHistoryMarkers : markersToDisplay
              }
              popUpContent={popupContent}
              origin={origin}
              destination={destination}
              setActiveMarker={(value) => {
                setActiveMarker(value);
                setState((prevState) => ({
                  ...prevState,
                  selectedAsset: {},
                  selectedAssetId: "",
                }));
              }}
              setMapSettings={setMapSettings}
            />
          </Grid>

          {/* Tracker Pie Chart - Ideally we would break this into its own widget. But this was a late addition to the project. Turning this into its own widget causes re-renders when anything updates. */}
          {shouldShowPieChart ? (
            <Grid
              item
              size={4}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
                padding: "0 1rem",
              }}
            >
              <Card raised variant="outlined">
                <CardContent>
                  <Typography variant="h4">Available Trackers</Typography>
                  <Box sx={{ display: "flex" }}>
                    <PieChart
                      height={300}
                      series={series}
                      slotProps={{
                        legend: {
                          direction: "row",
                          position: {
                            vertical: "bottom",
                            horizontal: "middle",
                          },
                          padding: 0,
                        },
                      }}
                      // This margin right centeres the pie chart
                      margin={{ right: 0 }}
                      width={250}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ) : null}

          {/* Clear Selection Button - Date Pickers */}
          <Grid
            item
            size={12}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: ".5rem",
            }}
          >
            {/* Clear Selection Button */}
            <Box>
              <MaterialUiButton
                color="primary"
                disabled={!isClearSelectionButtonDisabled}
                label="CLEAR SELECTION"
                onClick={clearSelection}
                size="small"
                variant="text"
              />
            </Box>
          </Grid>

          {/* Table */}
          <Grid item size={12} sx={{ maxHeight: "10vh" }}>
            <MVPAssetsTable
              onClick={handleRowClick}
              handleRowClick={handleRowClick}
              state={state}
              timeZone={timeZone}
              zones={zones}
            />
          </Grid>
        </>
      ) : (
        <IndoorMap
          {...props}
          activeMarker={activeMarker}
          activeFacility={activeFacility}
          convertPayloadToMarkers={convertPayloadToMarkers}
          facilityId={activeFacility}
          floorPlanData={floorPlanData}
          handleMarkerClick={handleMarkerClick}
          initialAssets={initialResults}
          mapSettings={mapSettings}
          organizationsZonesMap={zones}
          origin={origin} //replace w facility related data
          polygonsToRender={polygonsToRender}
          popupContent={popupContent}
          selectedAsset={selectedAsset}
          setActiveMarker={setActiveMarker}
          setMapSettings={setMapSettings}
          setParentState={setState}
          setShowGoogleMap={setShowGoogleMap}
          showGoogleMap={showGoogleMap}
          zones={zones}
        />
      )}
    </Grid>
  );
}
